import { put, call, all } from "redux-saga/effects";
import {
  isAdmin,
  showError,
  showWarning,
  showSuccess,
  showInfo,
  hideNotification
} from "../utils/utils";
import { pdApiActions } from "../actions/pdApiActions";
import { mainActions } from "../actions/mainActions";
import { pdApiGet, pdApiDownload, pdApiPost } from "../utils/pdApi";
import { ioConfigActions } from "../actions/ioConfigActions";
import { logout } from "../components/AuthProvider/AuthProvider";


export const prioritizeLoadingSequence = (
  list: Api.GroupName[],
  priorityDomainGroup: Api.GroupName
): Api.GroupName[] => {
  list.splice(list.indexOf(priorityDomainGroup), 1);
  list.unshift(priorityDomainGroup);
  return list;
};
export const defaultParameterFetchingOrder: Api.GroupName[] = [
  "Lift",
  "Group",
  "Safety",
  "Door"
];
/* Fetch parameters*/
export function* fetchParametersSaga(): Api.AnyItem {
  // TODO Prioritize order based on user roles when they are implemented (e.g. userRole is Lift -> load liftParameters first)
  // const activeUser: Api.User | undefined = yield select(mainSelector.activeUser);
  const userMainDomainGroupName: Api.GroupName | undefined = undefined; // map user roles to domainGroupNames here
  const prioritizedParameterFetchingOrder = userMainDomainGroupName
    ? prioritizeLoadingSequence(defaultParameterFetchingOrder, userMainDomainGroupName)
    : defaultParameterFetchingOrder;

  yield put(pdApiActions.setParametersLoadingStatus(true));
  try {
    yield call(fetchParameter, prioritizedParameterFetchingOrder[0]);
    yield call(fetchParameter, prioritizedParameterFetchingOrder[1]);
    yield call(fetchParameter, prioritizedParameterFetchingOrder[2]);
    yield call(fetchParameter, prioritizedParameterFetchingOrder[3]);
    yield put(showSuccess("All parameters loaded"));
  } catch (e) {
    yield put(showError("Parameter loading failed"));
  } finally {
    yield put(pdApiActions.setParametersLoadingStatus(false));
  }
}

export function* fetchDriveParameters(): Api.AnyItem {
  yield put(pdApiActions.driveParametersLoadingStatus(true));
  try {
    yield call(fetchParameter, "Drive");
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.driveParametersLoadingStatus(false));
  }
}

export function* fetchParameter(domainGroupName: Api.GroupName): Api.AnyItem {
  try {
    const filterString = "?domainGroupName=" + domainGroupName.toLocaleLowerCase();
    const parameters =
      domainGroupName === "Door"
        ? yield call(pdApiGet, "doorParameters", undefined)
        : yield call(pdApiGet, "parameters", undefined, filterString);

    if (domainGroupName === "Lift") {
      yield put(pdApiActions.setLiftParameters(parameters));
    } else if (domainGroupName === "Drive") {
      yield put(pdApiActions.setDriveParameters(parameters));
    } else if (domainGroupName === "Group") {
      yield put(pdApiActions.setGroupParameters(parameters));
    } else if (domainGroupName === "Safety") {
      yield put(pdApiActions.setSafetyParameters(parameters));
    } else if (domainGroupName === "Door") {
      yield put(pdApiActions.setDoorParameters(parameters));
    } else {
      const errorMsg = { message: "Domain Group name invalid: " + domainGroupName };
      throw errorMsg;
    }
  } catch (e) {
    yield put(showError("Error loading " + domainGroupName + " parameters"));
  }
}
/* Fetch faults*/
export function* fetchFaultsSaga(): Api.AnyItem {
  yield put(pdApiActions.setFaultsLoadingStatus(true));

  try {
    const allFaults = yield call(pdApiGet, "faults");
    yield put(pdApiActions.setFaults(allFaults));
    const doorFaults = yield call(pdApiGet, "doorFaults");
    yield put(pdApiActions.setDoorFaults(doorFaults));
    const nonKoneFaults = yield call(pdApiGet, "nonKoneFaults");
    yield put(pdApiActions.setNonKoneFaults(nonKoneFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(showSuccess("Fetching faults was successful"));
    yield put(pdApiActions.setFaultsLoadingStatus(false));
  }
}

/* Fetch data but only fetch active user in production environment */

export function* fetchDataSaga(): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Loading"));
  yield put(pdApiActions.setParametersLoadingStatus(true));
  yield put(pdApiActions.setFaultsLoadingStatus(true));

  let activeUser;

  try {
    activeUser = yield call(pdApiGet, "activeUser");
    yield put(mainActions.setActiveUser(activeUser));
  } catch (e) {
    if (e.message.includes("403")) {
      yield put(
        showWarning(
          "This account has no access to KCE Tools. Press OK to sign out and get back to login page.",
          {
            buttonText: "OK",
            buttonHandler: () => {
              logout();
            },
            autoDismiss: 30000
          }
        )
      );
    } else {
      yield put(showError(`${e}`));
    }
  }

  if (activeUser) {

    if (isAdmin(activeUser)) {
      try {
        const [users, roles, aadgroups] = yield all([
          call(pdApiGet, "users"),
          call(pdApiGet, "roles"),
          call(pdApiGet, "aadgroups")
        ]);

        yield put(pdApiActions.setUsers(users));
        yield put(pdApiActions.setRoles(roles));

        yield put(pdApiActions.setAADGroups(aadgroups));
      } catch (e) {
        yield put(showError(`${e}`));
      }
    }

    try {
      const [
        domainGroups,
        domains,
        features,
        faultPriorities,
        parameterCategories,
        faultCategories,
        userGroups,
        kceVersions,
        kceProducts,
        faultComponents,
        driveVersions,
        driveSignals,
        driveTests,
        signalGroups,
        eventClasses,
        eventTypes,
        eventPids,
        extCommands,
        messageTypes,
        ioBoardTypes,
        ioCpuEnums,
        ioNetworks,
        ioOptions,
        ioDatatypes,
        ioBasicDataTypes,
        iodataSections,
        ioConfigs,
        electrifications,
        enumerations,
        enumerationItems,
        locales,
        ioOptionsEnumType
      ] = yield all([
        call(pdApiGet, "domainGroups"),
        call(pdApiGet, "domains"),
        call(pdApiGet, "features"),
        call(pdApiGet, "faultPriorities"),
        call(pdApiGet, "parameterCategories"),
        call(pdApiGet, "faultCategories"),
        call(pdApiGet, "userGroups"),
        call(pdApiGet, "kceVersions"),
        call(pdApiGet, "kceProducts"),
        call(pdApiGet, "faultComponents"),
        call(pdApiGet, "driveVersions"),
        call(pdApiGet, "signals"),
        call(pdApiGet, "driveTests"),
        call(pdApiGet, "signalGroups"),
        call(pdApiGet, "eventClasses"),
        call(pdApiGet, "eventTypes"),
        call(pdApiGet, "eventPids"),
        call(pdApiGet, "extCommands"),
        call(pdApiGet, "messageTypes"),
        call(pdApiGet, "ioBoardTypes"),
        call(pdApiGet, "ioCpuEnums"),
        call(pdApiGet, "ioNetworks"),
        call(pdApiGet, "ioOptions"),
        call(pdApiGet, "ioDataTypes"),
        call(pdApiGet, "ioBasicDataTypes"),
        call(pdApiGet, "iodataSections"),
        call(pdApiGet, "ioConfigurations"),
        call(pdApiGet, "electrifications"),
        call(pdApiGet, "enumerations"),
        call(pdApiGet, "enumerationItems"),
        call(pdApiGet, "localizationids"),
        call(pdApiGet, "ioOptionsEnumType")
      ]);

      yield put(pdApiActions.setDomains(domains));
      yield put(pdApiActions.setFeatures(features));
      yield put(pdApiActions.setFaultPriorities(faultPriorities));
      yield put(pdApiActions.setParameterCategories(parameterCategories));
      yield put(pdApiActions.setFaultCategories(faultCategories));
      yield put(pdApiActions.setDomainGroups(domainGroups));
      yield put(pdApiActions.setUserGroups(userGroups));
      yield put(pdApiActions.setFaultComponents(faultComponents));
      yield put(pdApiActions.setKceVersions(kceVersions));
      yield put(pdApiActions.setKceProducts(kceProducts));
      yield put(pdApiActions.setDriveVersions(driveVersions));
      yield put(pdApiActions.setDriveSignals(driveSignals));
      yield put(pdApiActions.setDriveTests(driveTests));
      yield put(pdApiActions.setDriveSignalGroups(signalGroups));
      yield put(pdApiActions.setEventClasses(eventClasses));
      yield put(pdApiActions.setEventTypes(eventTypes));
      yield put(pdApiActions.setEventPids(eventPids));
      yield put(pdApiActions.setExtCommands(extCommands));
      yield put(pdApiActions.setMessageTypes(messageTypes));
      yield put(pdApiActions.setIOBoardTypes(ioBoardTypes));
      yield put(pdApiActions.setIOCpus(ioCpuEnums));
      yield put(pdApiActions.setIONetwork(ioNetworks));
      yield put(pdApiActions.setIOOptions(ioOptions));
      yield put(pdApiActions.setIODatatypes(ioDatatypes));
      yield put(pdApiActions.setIOBasicDatatypes(ioBasicDataTypes));
      yield put(pdApiActions.setIODataSections(iodataSections));
      yield put(ioConfigActions.setIoConfigurations(ioConfigs));
      yield put(pdApiActions.setElectrifications(electrifications));
      yield put(pdApiActions.setCommonEnumerations(enumerations));
      yield put(pdApiActions.setCommonEnumerationItems(enumerationItems));
      yield put(pdApiActions.setLocales(locales));
      yield put(pdApiActions.setIOEnumOptionsType(ioOptionsEnumType));
      yield put(showSuccess("Fetching data was successful", { id: "fetchedAllData" }));
      yield put(mainActions.setFetchingStatus(undefined)); // Params and faults have separate load indicators
      yield call(fetchParametersSaga);
      yield call(fetchFaultsSaga);
    } catch (e) {
      yield put(showError(`${e}`));
      yield put(mainActions.setFetchingStatus(undefined));
    }
  } else {
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* fetchAllOnClickSaga(): Api.AnyItem {
  try {
    const [escalatorParameters, escalatorFaults, escalatorRTDs, escalatorRTDGroups, escalatorVersions, escalatorEventLogs] = yield all([
      call(pdApiGet, "escalatorParameters"),
      call(pdApiGet, "escalatorFaults"),
      call(pdApiGet, "escalatorRTDs"),
      call(pdApiGet, "escalatorRTDGroups"),
      call(pdApiGet, "EscalatorVersions"),
      call(pdApiGet, "escalatorEventLogs")
    ]);

    yield put(pdApiActions.setEscalatorParameters(escalatorParameters));
    yield put(pdApiActions.setEscalatorFaults(escalatorFaults));
    yield put(pdApiActions.setEscalatorRTDs(escalatorRTDs));
    yield put(pdApiActions.setEscalatorRTDGroups(escalatorRTDGroups));
    yield put(pdApiActions.setEscalatorVersions(escalatorVersions));
    yield put(pdApiActions.setEscalatorEventLogs(escalatorEventLogs));
  } catch (e) {
    yield put(showError(`${e}`));
  }
}



export function* fetchAPMParamters(): Api.AnyItem {
  yield put(pdApiActions.apmParaLoadingStatus(true));
  try {
    const [ApmParamters] = yield all([
      call(pdApiGet, "parameters", undefined, "?domainGroupName=apm")
    ]);

    yield put(pdApiActions.setAPMParameters(ApmParamters));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.apmParaLoadingStatus(false));
  }
}

export function* fetchAPMFaults(): Api.AnyItem {
  yield put(pdApiActions.apmFaultsLoadingStatus(true));
  try {
    const [apmFaults] = yield all([
      call(pdApiGet, "apmFaults")
    ]);

    yield put(pdApiActions.setAPMFaults(apmFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.apmFaultsLoadingStatus(false));
  }
}

export function* fetchSignalizationFaults(): Api.AnyItem {
  yield put(pdApiActions.signalizationFaultsLoadingStatus(true));
  try {
    const [signalizationFaults] = yield all([
      call(pdApiGet, "signalizationFaults")
    ]);

    yield put(pdApiActions.setSignalizationFaults(signalizationFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.signalizationFaultsLoadingStatus(false));
  }
}

export function* fetchDataActCodes(): Api.AnyItem {
  yield put(pdApiActions.dataActCodesLoadingStatus(true));
  try {
    const [dataActs] = yield all([
      call(pdApiGet, "dataActs")
    ]);

    yield put(pdApiActions.setDataActCodes(dataActs));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.dataActCodesLoadingStatus(false));
  }
}

export function* fetchEscalatorDataActCodes(): Api.AnyItem {
  yield put(pdApiActions.escalatorDataActCodesLoadingStatus(true));
  try {
    const [escalatorDataActs] = yield all([
      call(pdApiGet, "escalatorDataActs")
    ]);

    yield put(pdApiActions.setEscalatorDataActCodes(escalatorDataActs));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.escalatorDataActCodesLoadingStatus(false));
  }
}

export function* fetchGrandNorms(): Api.AnyItem {
  yield put(pdApiActions.grandNormsLoadingStatus(true));

  try {
    const [grandNorms] = yield all([
      call(pdApiGet, "grandNorms")
    ]);

    yield put(pdApiActions.setGrandNorms(grandNorms));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.grandNormsLoadingStatus(false));
  }
}

export function* fetchDriveFaults(): Api.AnyItem {
  yield put(pdApiActions.driveFaultsLoadingStatus(true));
  try {
    const [driveFaults] = yield all([
      call(pdApiGet, "driveFaults")
    ]);

    yield put(pdApiActions.setDriveFaults(driveFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.driveFaultsLoadingStatus(false));
  }
}

export function* fetchLiftaults(): Api.AnyItem {
  yield put(pdApiActions.liftFaultsLoadingStatus(true));
  try {
    const [liftFaults] = yield all([
      call(pdApiGet, "liftFaults")
    ]);

    yield put(pdApiActions.setLiftFaults(liftFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.liftFaultsLoadingStatus(false));
  }
}

export function* fetchGroupFaults(): Api.AnyItem {
  yield put(pdApiActions.groupFaultsLoadingStatus(true));
  try {
    const [groupFaults] = yield all([
      call(pdApiGet, "groupFaults")
    ]);

    yield put(pdApiActions.setGroupFaults(groupFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.groupFaultsLoadingStatus(false));
  }
}

export function* fetchNetworkFaults(): Api.AnyItem {
  yield put(pdApiActions.networkFaultsLoadingStatus(true));
  try {
    const [networkFaults] = yield all([
      call(pdApiGet, "networkFaults")
    ]);

    yield put(pdApiActions.setNetworkFaults(networkFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.networkFaultsLoadingStatus(false));
  }
}

export function* fetchMonitoringFaults(): Api.AnyItem {
  yield put(pdApiActions.monitoringFaultsLoadingStatus(true));
  try {
    const [monitoringFaults] = yield all([
      call(pdApiGet, "monitoringFaults")
    ]);

    yield put(pdApiActions.setMonitoringFaults(monitoringFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.monitoringFaultsLoadingStatus(false));
  }
}

export function* fetchMonitoringFaultCategories(): Api.AnyItem {
  yield put(pdApiActions.monitoringFaultCategoriesLoadingStatus(true));
  try {
    const [monitoringFaultCategories] = yield all([
      call(pdApiGet, "monitoringFaultCategories")
    ]);

    yield put(pdApiActions.setMonitoringFaultCategories(monitoringFaultCategories));
  } catch (e) {
    yield put(showError(`${e}`));
  }
  finally {
    yield put(pdApiActions.monitoringFaultCategoriesLoadingStatus(false));
  }
}

/* Removing a drive signal group removes also signals that are assigned to it (db cascade), 
   so re-fetching the signals is done with this function */
export function* fetchSignalsSaga(): Api.AnyItem {
  yield put(mainActions.setFetchingStatus("Loading"));

  try {
    const [driveSignals] = yield all([call(pdApiGet, "signals")]);
    yield put(pdApiActions.setDriveSignals(driveSignals));
    yield put(showSuccess("Fetching drive signals was successful"));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(mainActions.setFetchingStatus(undefined));
  }
}

export function* fetchEscalatorRTDs(): Api.AnyItem {
  yield put(pdApiActions.escalatorRTDLoadingStatus(true));

  try {
    const escalatorRTDs = yield call(pdApiGet, "escalatorRTDs");
    yield put(pdApiActions.setEscalatorRTDs(escalatorRTDs));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorRTDLoadingStatus(false));
  }
}

export function* fetchEscalatorRTDGroups(): Api.AnyItem {
  yield put(pdApiActions.escalatorRTDGroupLoadingStatus(true));

  try {
    const escalatorRTDGroups = yield call(pdApiGet, "escalatorRTDGroups");
    yield put(pdApiActions.setEscalatorRTDGroups(escalatorRTDGroups));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorRTDGroupLoadingStatus(false));
  }
}

export function* fetchEscalatorEventLogs(): Api.AnyItem {
  yield put(pdApiActions.escalatorEventLogLoadingStatus(true));

  try {
    const escalatorEventLogs = yield call(pdApiGet, "escalatorEventLogs");
    yield put(pdApiActions.setEscalatorEventLogs(escalatorEventLogs));
    const enumerations = yield call(pdApiGet, "enumerations");
    yield put(pdApiActions.setCommonEnumerations(enumerations));
    const enumerationItems = yield call(pdApiGet, "enumerationItems");
    yield put(pdApiActions.setCommonEnumerationItems(enumerationItems));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorEventLogLoadingStatus(false));
  }
}

export function* fetchStandardBoardsShallowSaga(): Api.AnyItem {
  try {
    const standardBoards = yield call(pdApiGet, "standardBoards");
    yield put(ioConfigActions.setStandardBoards(standardBoards));
    const [decks, sides] = yield all([call(pdApiGet, "decks"), call(pdApiGet, "sides")]);
    yield put(pdApiActions.setDecks(decks));
    yield put(pdApiActions.setSides(sides));
  } catch (e) {
    yield put(showError(`${e}`));
  }
}

export function* fetchIoConfigurations(): Api.AnyItem {
  yield put(ioConfigActions.setLocalFetchingStatus("Fetching"));

  try {
    const ioConfigs = yield call(pdApiGet, "ioConfigurations");
    yield put(ioConfigActions.setIoConfigurations(ioConfigs));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(ioConfigActions.setLocalFetchingStatus(undefined));
  }
}
export function* fetchEscalatorParameters(): Api.AnyItem {
  yield put(pdApiActions.escalatorParaLoadingStatus(true));
  try {
    const escalatorParameters = yield call(pdApiGet, "escalatorParameters");
    yield put(pdApiActions.setEscalatorParameters(escalatorParameters));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorParaLoadingStatus(false));
  }
}
export function* fetchEscalatorFaults(): Api.AnyItem {
  yield put(pdApiActions.escalatorFaultLoadingStatus(true));

  try {
    const escalatorFaults = yield call(pdApiGet, "escalatorFaults");
    yield put(pdApiActions.setEscalatorFaults(escalatorFaults));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorFaultLoadingStatus(false));
  }
}

export function* fetchEscalatorEvent(action: ReturnType<typeof pdApiActions.fetchEscalatorEvent>): Api.AnyItem {
  yield put(pdApiActions.escalatorEventLoadingStatus(true));
  try {
    switch (action.payload) {
      case "Escalator Event Classes": {
        const escalatorEventClasses = yield call(pdApiGet, "EscalatorEventClasses");
        yield put(pdApiActions.setEscalatorEventClasses(escalatorEventClasses));
        break; // Add break statement to prevent fall-through
      }
      case "Escalator Event Types": {
        const escalatorEventTypes = yield call(pdApiGet, "EscalatorEventTypes");
        yield put(pdApiActions.setEscalatorEventTypes(escalatorEventTypes));
        break;
      }
      case "Escalator Event Pids": {
        const escalatorEventPids = yield call(pdApiGet, "EscalatorEventPids");
        yield put(pdApiActions.setEscalatorEventPids(escalatorEventPids));
        break;
      }
      case "Escalator Ext. Commands": {
        const escalatorExtCommand = yield call(pdApiGet, "EscalatorExtCommands");
        yield put(pdApiActions.setEscalatorExtCommand(escalatorExtCommand));
        break;
      }
      case "Escalator Request Types": {
        const escalatorRequestTypes = yield call(pdApiGet, "EscalatorRequestTypes");
        yield put(pdApiActions.setEscalatorRequestTypes(escalatorRequestTypes));
        break;
      }
      default:
        console.error("Unknown event type:", action.payload);
    }
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorEventLoadingStatus(false));
  }
}

export function* fetchDoorBindings(action: ReturnType<typeof pdApiActions.fetchDoorBindings>): Api.AnyItem {
  yield put(pdApiActions.doorBindingsLoadingStatus(true));
  try {
    switch (action.payload) {
      case "Door Versions": {
        const doorVersions = yield call(pdApiGet, "DoorVersions");
        yield put(pdApiActions.setDoorVersions(doorVersions));
        break; // Add break statement to prevent fall-through
      }
      default:
        console.error("Unknown event type:", action.payload);
    }
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.doorBindingsLoadingStatus(false));
  }
}

export function* fetchDoorEvent(action: ReturnType<typeof pdApiActions.fetchDoorEvent>): Api.AnyItem {
  yield put(pdApiActions.doorEventLoadingStatus(true));
  try {
    switch (action.payload) {
      case "Door Event Classes": {
        const doorEventClasses = yield call(pdApiGet, "doorEventClasses");
        yield put(pdApiActions.setDoorEventClasses(doorEventClasses));
        break;
      }
      case "Door Event Types": {
        const doorEventTypes = yield call(pdApiGet, "doorEventTypes");
        yield put(pdApiActions.setDoorEventTypes(doorEventTypes));
        break;
      }
      case "Door Event Pids": {
        const doorEventPids = yield call(pdApiGet, "doorEventPids");
        yield put(pdApiActions.setDoorEventPids(doorEventPids));
        break;
      }
      case "Door Ext. Commands": {
        const doorExtCommands = yield call(pdApiGet, "doorExtCommands");
        yield put(pdApiActions.setDoorExtCommands(doorExtCommands));
        break;
      }
      default:
        console.error("Unknown event type:", action.payload);
    }
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.doorEventLoadingStatus(false));
  }
}

export function* fetchEscalatorVersions(action: ReturnType<typeof pdApiActions.fetchEscalatorVersions>): Api.AnyItem {
  yield put(pdApiActions.escalatorVersionsLoadingStatus(true));
  try {
    switch (action.payload) {
      case "Escalator Versions": {
        const escalatorVersions = yield call(pdApiGet, "EscalatorVersions");
        yield put(pdApiActions.setEscalatorVersions(escalatorVersions));
        break; // Add break statement to prevent fall-through
      }
      default:
        console.error("Unknown event type:", action.payload);
    }
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.escalatorVersionsLoadingStatus(false));
  }
}

export function* downloadLocalizationFilesSaga(
  action: ReturnType<typeof pdApiActions.downloadLocalizationFile>
): Api.AnyItem {
  const { selectedDomainGroup, selectedLocale } = action.payload;

  let sagaFailed = false;
  let responseData = "";
  const startNotification = yield put(
    showInfo("Generating localization texts...", {
      id: "DownloadingLocalizationFiles",
      autoDismiss: 0
    })
  );

  try {
    responseData = yield call(
      pdApiDownload,
      "translations",
      "",
      "?type=" + selectedDomainGroup + "&locale=" + selectedLocale
    );
  } catch (e) {
    yield put(hideNotification(startNotification.uid));
    yield put(showError(`Error in generating localization texts : ${e}`));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      const url = window.URL.createObjectURL(new Blob([responseData]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `${selectedDomainGroup}_Localizations_${selectedLocale}.zip`);
      document.body.appendChild(link);
      link.click();

      yield put(hideNotification(startNotification.uid));
      yield put(showSuccess("Localization texts generated."));
    }
  }
}

export function* publishLocalizationFilesSaga(): Api.AnyItem {
  yield put(pdApiActions.setLocalizationsPublishingStatus(true));

  yield put(
    showInfo("Publishing localizations. This may take several minutes, you will be notified once it is done.", {
      id: "PublishingInBackground",
      autoDismiss: 10
    })
  );

  try {
    // Trigger the publish - backend returns 202 with instanceId, or 409 if already running
    let instanceId = "LocalizationFileCreation";

    try {
      const triggerResponse = yield call(pdApiPost, "localizations/trigger" as Api.OptionsGrud, {});
      instanceId = triggerResponse?.instanceId || "LocalizationFileCreation";
    } catch (triggerError: any) {
      if (triggerError?.response?.status === 409) {
        // Already running — just wait for the existing instance
        yield put(showInfo("Publishing is already in progress. You will be notified once it is done.", {
          id: "AlreadyPublishing",
          autoDismiss: 10
        }));
      } else {
        throw triggerError;
      }
    }

    // Single call that waits until the backend job completes
    const statusResponse = yield call(
      pdApiGet,
      `localizations/trigger/wait/${instanceId}` as Api.OptionsGet
    );

    const status = statusResponse?.status;

    if (status === "Completed") {
      yield put(pdApiActions.localizationPublishSucceeded(Date.now()));
      yield put(showSuccess("Localization files have been published successfully."));
    } else if (status === "Failed") {
      yield put(showError("Localization publishing failed"));
    } else {
      // Unexpected status — still trigger a refresh so the UI updates
      yield put(pdApiActions.localizationPublishSucceeded(Date.now()));
      yield put(showInfo("Localization publishing finished with status: " + (status || "unknown")));
    }
  } catch (e) {
    yield put(showError(`Error in publishing localizations: ${e}`));
  } finally {
    yield put(pdApiActions.setLocalizationsPublishingStatus(false));
  }
}

export function* exportDevelopmentPackageSaga(
  action: ReturnType<typeof pdApiActions.exportDevelopmentPackage>
): Api.AnyItem {
  const developmentPackage: Api.DevelopmentPackage = action.payload;
  let sagaFailed = false;
  let downloadUrl = "";

  yield put(pdApiActions.setExportDevelopmentPackageStatus(true));

  const createNotification = yield put(
    showInfo("Creating development package...", { id: "CreatingDPNotification", autoDismiss: 0 })
  );

  try {
    downloadUrl = yield call(
      pdApiGet,
      "code-export",
      "",
      "?configId=" + developmentPackage.id + "&languages=" + developmentPackage.languagesBitmask + "&doubleDecker=" + developmentPackage.typeDDMemory
    );
  } catch (e) {
    yield put(hideNotification(createNotification.uid));
    yield put(showError(`Error in exporting development package: ${e}`));
    yield put(pdApiActions.setExportDevelopmentPackageStatus(false));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      yield put(hideNotification(createNotification.uid));
      yield put(
        showSuccess("Development package ready. %%", {
          id: "DPReady",
          linkText: "Download.",
          linkUrl: downloadUrl,
          autoDismiss: 30
        })
      );
      yield put(pdApiActions.setExportDevelopmentPackageStatus(false));
    }
  }
}

export function* downloadEscalatorDevPackageSaga(
  action: ReturnType<typeof pdApiActions.downloadEscalatorDevPackage>
): Api.AnyItem {
  const escalatorDevPackage: Api.EscalatorDevPackage = action.payload;
  let sagaFailed = false;
  let downloadUrl = "";

  yield put(pdApiActions.setDownloadEscalatorDevPackage(true));

  const createNotification = yield put(
    showInfo(`Creating development package for Domain "${escalatorDevPackage.domainName}".`, { id: "CreatingDPNotification", autoDismiss: 0 })
  );

  try {
    downloadUrl = yield call(
      pdApiGet,
      "commonCode-export",
      "",
      "?domainGroupEnumValue=" + escalatorDevPackage.domainGroupEnumValue + 
      "&domainId=" + escalatorDevPackage.domainId + 
      "&languages=" + escalatorDevPackage.languages + 
      "&allParameters=" + escalatorDevPackage.allParameters + 
      "&versionId=" + escalatorDevPackage.versionId
    );
  } catch (e) {
    yield put(hideNotification(createNotification.uid));
    yield put(showError(`Error in exporting development package: ${e}`));
    yield put(pdApiActions.setDownloadEscalatorDevPackage(false));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      yield put(hideNotification(createNotification.uid));
      yield put(
        showSuccess(`Development package for "${escalatorDevPackage.domainName}" is ready. %%`, {
          id: "EscDPReady",
          linkText: "Download.",
          linkUrl: downloadUrl,
          autoDismiss: 30
        })
      );
      yield put(pdApiActions.setDownloadEscalatorDevPackage(false));
    }
  }
}

export function* convertBinaryIoConfigToStringSaga(
  action: ReturnType<typeof pdApiActions.convertBinaryIoConfigToString>
): Api.AnyItem {
  const ioConfig = action.payload;

  let sagaFailed = false;
  const createNotification = yield put(
    showInfo("Opening IO configuration file...", { id: "CreatingOBCFNotification", autoDismiss: 0 })
  );

  let result: Api.AnyItem;

  try {
    // Empty the contents of previous file
    yield put(ioConfigActions.updateIoConfigFileText(""));

    result = yield call(pdApiPost, "parseBinaryIoConfig", ioConfig, "application/octet-stream");
  } catch (e) {
    sagaFailed = true;

    yield put(hideNotification(createNotification.uid));
    yield put(showError(`Error in converting binary IO configuration file to string: ${e}`));
  } finally {
    if (sagaFailed === false) {
      yield put(hideNotification(createNotification.uid));
      yield put(ioConfigActions.updateIoConfigFileText(result));
      yield put(showSuccess("Successfully opened IO configuration file!"));
    }
  }
}

export function* fetchSingleSaga(action: ReturnType<typeof pdApiActions.fetchSingle>): Api.AnyItem {
  const option = action.payload.option;
  const id = action.payload.id;

  if (option === "ioConfigurations") {
    yield put(ioConfigActions.setLocalFetchingStatus("Fetching IO boards"));
  } else if (option === "ionodes") {
    yield put(ioConfigActions.setLocalFetchingStatus("Fetching Node Points"));
  } else {
    yield put(ioConfigActions.setLocalFetchingStatus("Fetching"));
  }

  try {
    const fetchedItem = yield call(pdApiGet, option, id);
    switch (option) {
      case "standardBoards":
        yield put(ioConfigActions.updateStandardPoints(fetchedItem));
        break;
      case "ioBoards":
        yield put(ioConfigActions.updateIoNodePoints(fetchedItem));
        break;
      case "ionodes": {
        const ioNode = fetchedItem as Api.IoNode;
        const ioNodePoints = ioNode.ionodePoints.map(np => ({ ...np, boardId: ioNode.boardId }));
        yield put(ioConfigActions.updateIoNodePoints(ioNodePoints));
        break;
      }
      case "ioConfigurations": {
        const ioBoards = fetchedItem.ioboards;
        const ioBoardsGrouped = fetchedItem.ioboardsGrouped;
        yield put(ioConfigActions.setIoBoards(ioBoards));
        yield put(ioConfigActions.setIoBoardsGrouped(ioBoardsGrouped));
        break;
      }
      default:
        console.error(
          "Something went wrong in fetchSingle saga. Propably forgot to add switch case "
        );
    }
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(ioConfigActions.setLocalFetchingStatus(undefined));
  }
}

export function* fetchTranslationLogItemsSaga(): Api.AnyItem | undefined {
  yield put(pdApiActions.setTranslationLogLoadingStatus(true));
  try {
    const fetchedItems: Api.TranslationLogItem[] = yield call(
      pdApiGet,
      "translationLog",
      undefined
    );
    yield put(pdApiActions.setTranslationLogItems(fetchedItems));
  } catch (e) {
    yield put(showError(`${e}`));
  } finally {
    yield put(pdApiActions.setTranslationLogLoadingStatus(false));
  }
}
export function* fetchChartDataSaga(
  action: ReturnType<typeof pdApiActions.fetchChartDataSingle>
): Api.AnyItem {
  const option = action.payload.option;
  const domainGroup = action.payload.domainGroup;
  const itemType = action.payload.itemType;
  const domain = action.payload.domain;

  try {
    switch (option) {
      case "translationSummary":
        {
          const fetchedItem: Api.SeriesDataset[] = yield call(
            pdApiGet,
            option,
            undefined,
            (domainGroup !== -1 || itemType !== 0 || domain !== -1 ? "?" : "") +
            (domainGroup !== -1 ? "domainGroup=" + domainGroup : "") +
            (itemType !== 0 ? "&itemType=" + itemType : "") +
            (domain !== -1 ? "&domain=" + domain : "")
          );
          yield put(pdApiActions.setTranslationSummary(fetchedItem));
        }
        break;
      case "languageCoverage":
        {
          const fetchedItem: Api.SeriesDataset[] = yield call(
            pdApiGet,
            option,
            undefined,
            (domainGroup !== -1 || itemType !== 0 || domain !== -1 ? "?" : "") +
            (domainGroup !== -1 ? "domainGroup=" + domainGroup : "") +
            (itemType !== 0 ? "&itemType=" + itemType : "") +
            (domain !== -1 ? "&domain=" + domain : "")
          );
          yield put(pdApiActions.setLanguageCoverage(fetchedItem));
        }
        break;
      case "itemTypeLanguageCoverage":
        {
          yield put(pdApiActions.setStatisticsLoadingStatus(true));
          const fetchedItem: Api.ItemTypeSeriesDataset[] = yield call(
            pdApiGet,
            option,
            undefined,
            (domainGroup !== -1 || itemType !== 0 || domain !== -1 ? "?" : "") +
            (domainGroup !== -1 ? "domainGroup=" + domainGroup : "") +
            (itemType !== 0 ? "&itemType=" + itemType : "") +
            (domain !== -1 ? "&domain=" + domain : "")
          );
          yield put(pdApiActions.setItemTypeLanguageCoverage(fetchedItem));
          yield put(pdApiActions.setStatisticsLoadingStatus(false));
        }
        break;
      default:
        console.error(
          "Something went wrong in fetchChartDataSingle saga. Propably forgot to add switch case "
        );
    }
  } catch (e) {
    yield put(pdApiActions.setTranslationSummary([]));
    yield put(pdApiActions.setLanguageCoverage([]));
    yield put(pdApiActions.setStatisticsLoadingStatus(false));
    if (!e.message.includes("400")) {
      yield put(showError(`${e}`));
    }
  }
}

export function* generateLicenseDataSaga(): Api.AnyItem {
  let sagaFailed = false;
  let response = null;

  const generateNotification = yield put(
    showInfo("Started generating license data", { autoDismiss: 0 })
  );

  try {
    yield put(pdApiActions.setLicenseGenerationStatus(true));
    response = yield call(pdApiGet, "licenseGeneration", "", "");
  } catch (e) {
    yield put(hideNotification(generateNotification.uid));
    yield put(showError(`Error in generating license data: ${e}`));
    yield put(pdApiActions.setLicenseGenerationStatus(false));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      yield put(hideNotification(generateNotification.uid));
      if (response.message) {
        yield put(showSuccess(`${response.message.replace(/\.$/, "")} for ${response.userName}`, { autoDismiss: 10 }));
      } else {
        yield put(showSuccess("License data generated"));
      }
      yield put(pdApiActions.setLicenseGenerationStatus(false));
    }
  }
}
export function* generateEscalatorLicenseDataSaga(): Api.AnyItem {
  let sagaFailed = false;
  let response = null;

  const generateNotification = yield put(
    showInfo("Started generating license data", { autoDismiss: 0 })
  );

  try {
    yield put(pdApiActions.setLicenseGenerationStatus(true));
    response = yield call(pdApiGet, "EscalatorLicenseGeneration", "", "");
  } catch (e) {
    yield put(hideNotification(generateNotification.uid));
    yield put(showError(`Error in generating license data: ${e}`));
    yield put(pdApiActions.setLicenseGenerationStatus(false));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      yield put(hideNotification(generateNotification.uid));
      if (response.message) {
        yield put(showSuccess(`${response.message.replace(/\.$/, "")} for ${response.userName}`, { autoDismiss: 10 }));
      } else {
        yield put(showSuccess("License data generated"));
      }
      yield put(pdApiActions.setLicenseGenerationStatus(false));
    }
  }
}
export function* generateDoorLicenseDataSaga(): Api.AnyItem {
  let sagaFailed = false;
  let response = null;

  const generateNotification = yield put(
    showInfo("Started generating license data", { autoDismiss: 0 })
  );

  try {
    yield put(pdApiActions.setLicenseGenerationStatus(true));
    response = yield call(pdApiGet as any, "DoorLicenseGeneration", "", "");
  } catch (e) {
    yield put(hideNotification(generateNotification.uid));
    yield put(showError(`Error in generating license data: ${e}`));
    yield put(pdApiActions.setLicenseGenerationStatus(false));
    sagaFailed = true;
  } finally {
    if (!sagaFailed) {
      yield put(hideNotification(generateNotification.uid));
      if (response.message) {
        yield put(showSuccess(`${response.message.replace(/\.$/, "")} for ${response.userName}`, { autoDismiss: 10 }));
      } else {
        yield put(showSuccess("License data generated"));
      }
      yield put(pdApiActions.setLicenseGenerationStatus(false));
    }
  }
}

// ─── License Upload Sagas ────────────────────────────────────────────────────

export function* uploadToTlgQaSaga(
  action: ReturnType<typeof pdApiActions.uploadTlgQa>
): Api.AnyItem {
  const { deviceType, userGroup, jsonContent } = action.payload;
  try {
    const formData = new FormData();
    const blob = new Blob([jsonContent], { type: "application/json" });
    formData.append("file", blob, "license.json");

    const url = `licenseUpload/${deviceType}/${userGroup}`;
    const result = yield call(pdApiPost, url as any, formData, "multipart/form-data");
    yield put(pdApiActions.uploadTlgQaSuccess(result));
    yield put(showSuccess("JSON uploaded to TLG QA successfully"));
  } catch (e) {
    const errorMsg = e.message || "Upload failed";
    yield put(pdApiActions.uploadTlgQaFailure(errorMsg));
    yield put(showError(`Upload to TLG QA failed: ${errorMsg}`));
  }
}

export function* fetchLicenseRecordSaga(
  action: ReturnType<typeof pdApiActions.fetchLicenseRecord>
): Api.AnyItem {
  const { deviceType, userGroup, db } = action.payload;
  try {
    const url = `licenseUpload/${deviceType}/${userGroup}`;
    const result = yield call(pdApiGet, url as any, undefined, `?db=${db}`);
    const record: State.RecordMeta | null = result && result.id ? {
      id: result.id,
      savedAt: result.savedAt,
      savedBy: result.savedBy,
    } : null;
    const jsonContent = result ? result.jsonContent || null : null;
    yield put(pdApiActions.fetchLicenseRecordSuccess(db, record, jsonContent));
  } catch (e) {
    const errorMsg = e.message || "Fetch failed";
    yield put(pdApiActions.fetchLicenseRecordFailure(db, errorMsg));
  }
}

export function* pushToProdSaga(
  action: ReturnType<typeof pdApiActions.pushToProd>
): Api.AnyItem {
  const { deviceType, userGroup } = action.payload;
  try {
    const url = `licenseUpload/${deviceType}/${userGroup}/push-prod`;
    const result = yield call(pdApiPost, url as any, {});
    yield put(pdApiActions.pushToProdSuccess(result));
    yield put(showSuccess("Pushed to TLG Prod successfully"));
  } catch (e) {
    const errorMsg = e.message || "Push to Prod failed";
    yield put(pdApiActions.pushToProdFailure(errorMsg));
    yield put(showError(`Push to Prod failed: ${errorMsg}`));
  }
}

export function* requestLotaSaga(
  action: ReturnType<typeof pdApiActions.requestLota>
): Api.AnyItem {
  const { deviceType, userGroup, comment } = action.payload;
  try {
    const url = `licenseUpload/${deviceType}/${userGroup}/request-lota`;
    const result = yield call(pdApiPost, url as any, { comment });
    yield put(pdApiActions.requestLotaSuccess(result.requestedBy, result.requestedAt));
    yield put(showSuccess("LOTA push requested"));
  } catch (e) {
    const errorMsg = e.message || "Request LOTA failed";
    yield put(pdApiActions.requestLotaFailure(errorMsg));
    yield put(showError(`Request LOTA failed: ${errorMsg}`));
  }
}

export function* approveLotaSaga(
  action: ReturnType<typeof pdApiActions.approveLota>
): Api.AnyItem {
  const { deviceType, userGroup } = action.payload;
  try {
    const url = `licenseUpload/${deviceType}/${userGroup}/approve-lota`;
    const result = yield call(pdApiPost, url as any, {});
    yield put(pdApiActions.approveLotaSuccess(result));
    yield put(showSuccess("LOTA push approved"));
  } catch (e) {
    const errorMsg = e.message || "Approve LOTA failed";
    yield put(pdApiActions.approveLotaFailure(errorMsg));
    yield put(showError(`Approve LOTA failed: ${errorMsg}`));
  }
}

export function* rejectLotaSaga(
  action: ReturnType<typeof pdApiActions.rejectLota>
): Api.AnyItem {
  const { deviceType, userGroup, reason } = action.payload;
  try {
    const url = `licenseUpload/${deviceType}/${userGroup}/reject-lota`;
    const result = yield call(pdApiPost, url as any, { reason });
    yield put(pdApiActions.rejectLotaSuccess(result.rejectionReason || reason));
    yield put(showSuccess("LOTA push rejected"));
  } catch (e) {
    const errorMsg = e.message || "Reject LOTA failed";
    yield put(pdApiActions.rejectLotaFailure(errorMsg));
    yield put(showError(`Reject LOTA failed: ${errorMsg}`));
  }
}
